import { useSession, useTenant } from '@zhiyue/core';
import React from 'react';
import {
  readCurrentParkUuid,
  readCurrentTenantUuid,
  saveCurrentParkUuid,
} from '@/adapters/tenantStorage';
import { ROUTES } from '@/constants/routes';
import { parkApi } from '@/services/bff/park.api';
import type {
  OperatorParkBootstrapStatus,
  Park,
} from '@/services/bff/types/park';

export enum ParkBootstrapStatus {
  INITIALIZING = 'INITIALIZING',
  NO_PARK = 'NO_PARK',
  NO_PARK_READONLY = 'NO_PARK_READONLY',
  NO_AVAILABLE_PARK = 'NO_AVAILABLE_PARK',
  PARK_NOT_SELECTED = 'PARK_NOT_SELECTED',
  READY = 'READY',
  LOAD_ERROR = 'LOAD_ERROR',
}

export const getParkBootstrapRoute = (status: ParkBootstrapStatus) => {
  if (
    status === ParkBootstrapStatus.NO_PARK ||
    status === ParkBootstrapStatus.NO_PARK_READONLY ||
    status === ParkBootstrapStatus.NO_AVAILABLE_PARK
  ) {
    return ROUTES.park.setup;
  }
  if (status === ParkBootstrapStatus.PARK_NOT_SELECTED)
    return ROUTES.park.select;
  if (status === ParkBootstrapStatus.LOAD_ERROR) return ROUTES.park.error;
  return ROUTES.overview;
};

export const shouldShowOperatorSider = (status: ParkBootstrapStatus) =>
  status === ParkBootstrapStatus.READY;

export interface ParkBootstrapState {
  status: ParkBootstrapStatus;
  tenantUuid: string | null;
  parks: Park[];
  activeParks: Park[];
  hasAnyPark: boolean;
  currentParkUuid: string | null;
  canCreatePark: boolean;
  loading: boolean;
  loaded: boolean;
  error: unknown;
}

const initialState: ParkBootstrapState = {
  status: ParkBootstrapStatus.INITIALIZING,
  tenantUuid: null,
  parks: [],
  activeParks: [],
  hasAnyPark: false,
  currentParkUuid: null,
  canCreatePark: false,
  loading: false,
  loaded: false,
  error: null,
};

let state = initialState;
let requestSequence = 0;
let activeRequest: Promise<Park[]> | null = null;
const listeners = new Set<() => void>();

const emit = () => {
  listeners.forEach((listener) => {
    listener();
  });
};

const setState = (next: ParkBootstrapState) => {
  state = next;
  emit();
};

export const isActivePark = (park: Park) =>
  park.status === 'active' && !park.isDeleted;

export const getActiveParks = (parks: Park[]) => parks.filter(isActivePark);

export const deriveParkBootstrapStatus = ({
  hasAnyPark,
  accessibleParks,
  currentParkUuid,
  canCreatePark,
}: {
  hasAnyPark: boolean;
  accessibleParks: Park[];
  currentParkUuid: string | null;
  canCreatePark: boolean;
}): ParkBootstrapStatus => {
  const activeParks = getActiveParks(accessibleParks);
  if (activeParks.length === 0) {
    if (hasAnyPark) return ParkBootstrapStatus.NO_AVAILABLE_PARK;
    return canCreatePark
      ? ParkBootstrapStatus.NO_PARK
      : ParkBootstrapStatus.NO_PARK_READONLY;
  }
  return currentParkUuid &&
    activeParks.some((park) => park.uuid === currentParkUuid)
    ? ParkBootstrapStatus.READY
    : ParkBootstrapStatus.PARK_NOT_SELECTED;
};

export const getParkBootstrapState = () => state;

export const subscribeParkBootstrap = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

const notifyParkContextChanged = () => {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event('park-context-changed'));
  }
};

const synchronizeCurrentPark = (
  tenantUuid: string,
  bootstrapStatus: OperatorParkBootstrapStatus,
) => {
  const { hasAnyPark, canCreatePark, accessibleParks } = bootstrapStatus;
  const activeParks = getActiveParks(accessibleParks);
  const stored = readCurrentParkUuid(tenantUuid);
  const currentParkUuid = activeParks.some((park) => park.uuid === stored)
    ? stored
    : null;

  if (stored !== currentParkUuid) {
    saveCurrentParkUuid(tenantUuid, currentParkUuid);
    notifyParkContextChanged();
  }

  return {
    activeParks,
    currentParkUuid,
    status: deriveParkBootstrapStatus({
      hasAnyPark,
      accessibleParks,
      currentParkUuid,
      canCreatePark,
    }),
    hasAnyPark,
    canCreatePark,
  };
};

export const selectOperatorPark = (tenantUuid: string, parkUuid: string) => {
  if (!tenantUuid || !parkUuid) return false;
  if (state.tenantUuid !== tenantUuid) return false;
  if (!state.activeParks.some((park) => park.uuid === parkUuid)) return false;

  saveCurrentParkUuid(tenantUuid, parkUuid);
  setState({
    ...state,
    currentParkUuid: parkUuid,
    status: ParkBootstrapStatus.READY,
  });
  notifyParkContextChanged();
  return true;
};

export const loadOperatorParks = async (
  tenantUuid?: string | null,
  force = false,
) => {
  const targetTenantUuid = tenantUuid || readCurrentTenantUuid();
  if (!targetTenantUuid) {
    setState({ ...initialState, loaded: true });
    return [];
  }

  if (
    !force &&
    state.tenantUuid === targetTenantUuid &&
    (state.loading || state.loaded)
  ) {
    return activeRequest || state.parks;
  }

  const sequence = ++requestSequence;
  setState({
    status: ParkBootstrapStatus.INITIALIZING,
    tenantUuid: targetTenantUuid,
    parks: [],
    activeParks: [],
    hasAnyPark: false,
    currentParkUuid: readCurrentParkUuid(targetTenantUuid),
    canCreatePark: false,
    loading: true,
    loaded: false,
    error: null,
  });

  const bootstrapRequest = parkApi.getBootstrapStatus();
  activeRequest = bootstrapRequest.then(
    ({ accessibleParks }) => accessibleParks,
  );
  void activeRequest.catch(() => undefined);
  try {
    const bootstrapStatus = await bootstrapRequest;
    const parks = bootstrapStatus.accessibleParks;
    if (sequence !== requestSequence) return parks;

    const synchronized = synchronizeCurrentPark(
      targetTenantUuid,
      bootstrapStatus,
    );
    setState({
      status: synchronized.status,
      tenantUuid: targetTenantUuid,
      parks,
      activeParks: synchronized.activeParks,
      hasAnyPark: synchronized.hasAnyPark,
      currentParkUuid: synchronized.currentParkUuid,
      canCreatePark: synchronized.canCreatePark,
      loading: false,
      loaded: true,
      error: null,
    });
    return parks;
  } catch (error) {
    if (sequence === requestSequence) {
      setState({
        status: ParkBootstrapStatus.LOAD_ERROR,
        tenantUuid: targetTenantUuid,
        parks: [],
        activeParks: [],
        hasAnyPark: false,
        currentParkUuid: readCurrentParkUuid(targetTenantUuid),
        canCreatePark: false,
        loading: false,
        loaded: true,
        error,
      });
    }
    throw error;
  } finally {
    if (sequence === requestSequence) activeRequest = null;
  }
};

export const useParkBootstrap = () => {
  const { currentUser } = useSession();
  const { currentTenantUuid } = useTenant();
  const [tenantUuid, setTenantUuid] = React.useState<string | null>(
    () =>
      currentTenantUuid ||
      readCurrentTenantUuid() ||
      currentUser?.tenantUuid ||
      null,
  );
  const snapshot = React.useSyncExternalStore(
    subscribeParkBootstrap,
    getParkBootstrapState,
    getParkBootstrapState,
  );

  const resolvedTenantUuid = currentTenantUuid || tenantUuid;
  React.useEffect(() => {
    const syncTenant = () => {
      setTenantUuid(
        currentTenantUuid ||
          readCurrentTenantUuid() ||
          currentUser?.tenantUuid ||
          null,
      );
    };
    syncTenant();
    window.addEventListener('tenant-context-changed', syncTenant);
    return () =>
      window.removeEventListener('tenant-context-changed', syncTenant);
  }, [currentTenantUuid, currentUser?.tenantUuid]);

  React.useEffect(() => {
    if (resolvedTenantUuid) {
      void loadOperatorParks(resolvedTenantUuid, false).catch(() => undefined);
    }
  }, [resolvedTenantUuid]);

  const refresh = React.useCallback(
    () => loadOperatorParks(resolvedTenantUuid, true),
    [resolvedTenantUuid],
  );

  const snapshotMatchesTenant = snapshot.tenantUuid === resolvedTenantUuid;

  return {
    ...snapshot,
    status: snapshotMatchesTenant
      ? snapshot.status
      : ParkBootstrapStatus.INITIALIZING,
    tenantUuid: resolvedTenantUuid,
    parks: snapshotMatchesTenant ? snapshot.parks : [],
    activeParks: snapshotMatchesTenant ? snapshot.activeParks : [],
    hasAnyPark: snapshotMatchesTenant ? snapshot.hasAnyPark : false,
    loading: snapshotMatchesTenant ? snapshot.loading : true,
    loaded: snapshotMatchesTenant && snapshot.loaded,
    error: snapshotMatchesTenant ? snapshot.error : null,
    canCreatePark: snapshotMatchesTenant ? snapshot.canCreatePark : false,
    currentParkUuid: snapshotMatchesTenant
      ? snapshot.currentParkUuid
      : readCurrentParkUuid(resolvedTenantUuid),
    refresh,
  };
};

export const resetParkBootstrapState = () => {
  requestSequence += 1;
  activeRequest = null;
  state = initialState;
  emit();
};

export const useOperatorParks = useParkBootstrap;
